import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import StarRoundedIcon from "@material-ui/icons/StarRounded";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";

import { i18n } from "../../translate/i18n";
import KpiCard from "./KpiCard";
import ReportTable from "./ReportTable";
import { formatRate, percent } from "./format";

// De 1 (rojo) a 5 (verde): el color ya dice si la nota es buena.
const STAR_COLORS = ["#DC2626", "#F97316", "#F59E0B", "#84CC16", "#16A34A"];

const useStyles = makeStyles((theme) => ({
  grid: { display: "grid", gap: 16 },
  kpis: { display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" },
  card: {
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    padding: "16px 18px",
  },
  cardTitle: { fontSize: 13, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", marginBottom: 12 },
  emptyState: {
    borderRadius: 12,
    border: `1px dashed ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    padding: "40px 24px",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 10,
  },
  emptyIcon: { fontSize: 44, color: "#F59E0B" },
  emptyTitle: { fontSize: 18, fontWeight: 700, margin: 0 },
  emptyText: { fontSize: 14, color: theme.palette.text.secondary, maxWidth: 520, margin: 0 },
  notice: {
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: 13,
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.secondary,
  },
}));

/**
 * Pestaña «Satisfacción» (doc 0447, D5): la encuesta 1–5 que se envía al cerrar.
 * Apagada y sin historial → explica qué mide y lleva a Opciones, en vez de un cero mudo.
 */
const CsatTab = ({ data }) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const c = data.csat;

  const distribution = useMemo(
    () =>
      [5, 4, 3, 2, 1].map((stars) => ({
        stars,
        name: "★".repeat(stars),
        count: c ? c.distribution[stars - 1] : 0,
      })),
    [c]
  );

  const agentRows = useMemo(
    () =>
      (data.csatAgents || []).map((a) => ({
        ...a,
        key: a.userId ?? "none",
        label: a.userId === null ? i18n.t("reports.team.unassigned") : a.userName,
      })),
    [data.csatAgents]
  );

  if (!c) return null;

  const goToOptions = () => history.push("/settings?tab=options");

  if (c.answered === 0) {
    return (
      <div className={classes.emptyState}>
        <StarRoundedIcon className={classes.emptyIcon} />
        <h3 className={classes.emptyTitle}>
          {c.ratingEnabled ? i18n.t("reports.csat.noAnswersTitle") : i18n.t("reports.csat.disabledTitle")}
        </h3>
        <p className={classes.emptyText}>
          {c.ratingEnabled
            ? i18n.t("reports.csat.noAnswersText", { sent: c.surveysSent })
            : i18n.t("reports.csat.disabledText")}
        </p>
        {!c.ratingEnabled && (
          <Button variant="contained" color="primary" onClick={goToOptions} style={{ marginTop: 6 }}>
            {i18n.t("reports.csat.enable")}
          </Button>
        )}
      </div>
    );
  }

  const columns = [
    { key: "label", label: i18n.t("reports.team.agent"), sort: (r) => String(r.label || "") },
    { key: "answered", label: i18n.t("reports.csat.answered"), align: "right" },
    { key: "avgRate", label: i18n.t("reports.csat.avgRate"), align: "right", sort: (r) => r.avgRate ?? -1, render: (r) => formatRate(r.avgRate) },
    {
      key: "satisfiedRate",
      label: i18n.t("reports.csat.satisfied"),
      align: "right",
      sort: (r) => percent(r.satisfied, r.answered),
      render: (r) => `${percent(r.satisfied, r.answered)}%`,
    },
    { key: "unsatisfied", label: i18n.t("reports.csat.unsatisfied"), align: "right" },
  ];

  return (
    <div className={classes.grid}>
      {!c.ratingEnabled && <div className={classes.notice}>{i18n.t("reports.csat.disabledNotice")}</div>}

      <div className={classes.kpis}>
        <KpiCard label={i18n.t("reports.csat.avgRate")} value={`${formatRate(c.avgRate)} / 5`} hint={i18n.t("reports.csat.answeredCount", { count: c.answered })} />
        <KpiCard label={i18n.t("reports.csat.satisfied")} value={`${percent(c.satisfied, c.answered)}%`} hint={i18n.t("reports.csat.satisfiedHint")} />
        <KpiCard label={i18n.t("reports.csat.unsatisfied")} value={c.unsatisfied} hint={i18n.t("reports.csat.unsatisfiedHint")} />
        <KpiCard
          label={i18n.t("reports.csat.responseRate")}
          value={`${Math.min(100, percent(c.answered, c.surveysSent))}%`}
          hint={i18n.t("reports.csat.responseRateHint", { answered: c.answered, sent: c.surveysSent })}
        />
      </div>

      <div className={classes.card}>
        <div className={classes.cardTitle}>{i18n.t("reports.csat.distribution")}</div>
        <ResponsiveContainer width="100%" height={210}>
          <BarChart data={distribution} layout="vertical" margin={{ top: 0, right: 24, left: 8, bottom: 0 }}>
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: theme.palette.text.secondary }} />
            <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 14, fill: "#F59E0B" }} />
            <Tooltip />
            <Bar dataKey="count" name={i18n.t("reports.csat.answered")} radius={[0, 4, 4, 0]}>
              {distribution.map((d) => <Cell key={d.stars} fill={STAR_COLORS[d.stars - 1]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <ReportTable title={i18n.t("reports.csat.byAgent")} columns={columns} rows={agentRows} defaultSort="answered" rowKey="key" />
    </div>
  );
};

export default CsatTab;
